import { Card, EmptyState, Table, TableColumns, Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import colors from '../../../assets/colors/Colors.json';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { BillingMethods, use_ContractsQuery } from '../../../types/planung-graphql-client-defs';
import { formatNumber } from '../../../utils/helper';
import { _evalBillingMethod } from '../../Lessons/Tables/Teachers/utils/eval-billing-method';
import styles from './DeputateDetailsTeacherOverview.module.scss';

export type DeputatesDetailTableData = {
  teacherUuid: string;
  teacherDisplayName: string;
  subjectUuid: string;
  comment?: string;
  subjectDisplayName: string;
  subjectContainerUuid?: string | null;
  subjectContainerDisplayName?: string | null;
  partialClasses: boolean;
  lessonClassesDisplayName: string;
  countClasses: number;
  countTeacher: number;
  studentCards?: number | null;
  teacherCards?: number | null;
  studentHours?: number | null;
  teacherHours?: number | null;
  teachingBlockWeeks?: number | null;
  teachingBlocks?: number | null;
  factor: number;
  deputat: number;
  timetableBlockUuid?: string | null;
  timetableBlockDisplayName?: string | null;
  timetableBlockSortName?: string | null;
};

export type DeputateDetailsOverviewData = {
  subjectLessons: Array<DeputatesDetailTableData> | undefined;
  sumSubjectLessons: number;
  additional: DeputatesDetailTableData[];
  subjectContainerLessons: Map<string, Array<DeputatesDetailTableData>>;
  sumSubjectContainerLessons: Map<string, number>;
  sumAdditional: number;
  sumTotal: number;
};

export const DeputateDetailsTeacherOverview = observer(
  ({ deputateData }: { deputateData: DeputateDetailsOverviewData }) => {
    const { t } = useTranslation();

    const { teacherUuid } = useParams();

    const { teacherData } = useLoadBasicData({ pause: !teacherUuid });

    const [{ data: contractData }] = use_ContractsQuery({
      pause: false,
    });

    const currentTeacher = teacherData?.people.find((t) => t.uuid === teacherUuid);
    const currentContract = contractData?.contracts.find((c) => c.person.uuid === currentTeacher?.uuid);
    const color = colors.find((c) => c.html === currentTeacher?.timetableConfig?.color);

    const detailColumns: TableColumns<DeputatesDetailTableData>[] = [
      {
        header: t('lesson.table.subject'),
        id: 'name',
        accessorKey: 'subjectDisplayName',
        size: 350,
        cell: ({ row }) => {
          const subjectName = row.original.subjectDisplayName;
          const blockName = row.original.timetableBlockDisplayName;
          return blockName ? `${subjectName} (${blockName})` : subjectName;
        },
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'lessonClassesDisplayName',
        accessorKey: 'lessonClassesDisplayName',
        size: 200,
      },
      {
        header: t('deputate.partialClass'),
        id: 'partialClass',
        accessorFn: (row) => (row.partialClasses ? 1 : 0),
        size: 125,
        type: 'boolean',
      },
      {
        header: t('persons.title', { count: 2 }),
        id: 'countTeacher',
        accessorKey: 'countTeacher',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('subjectHours.studentHours'),
        accessorFn: (row) => formatNumber(row.studentHours ?? 0),
        id: 'studentHours',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('subjectHours.title', { count: 2 }),
        accessorFn: (row) => formatNumber(row.studentCards ?? 0),
        id: 'studentCards',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('factors.title', { count: 1 }),
        accessorFn: (row) => (row.factor > 0 ? formatNumber(row.factor) : '-'),
        id: 'factor',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('deputate.title', { count: 1 }),
        accessorFn: (row) => formatNumber(row.deputat),
        id: 'deputat',
        size: 90,
        alignment: 'right',
      },
    ];

    const containerColumns: TableColumns<DeputatesDetailTableData>[] = [
      {
        header: t('lesson.table.subject'),
        id: 'name',
        accessorKey: 'subjectDisplayName',
        size: 350,
        cell: ({ row }) => {
          const subjectName = row.original.subjectDisplayName;
          const blockName = row.original.timetableBlockDisplayName;
          return blockName ? `${subjectName} (${blockName})` : subjectName;
        },
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'lessonClassesDisplayName',
        accessorKey: 'lessonClassesDisplayName',
        size: 200,
      },
      {
        header: t('deputate.partialClass'),
        id: 'partialClass',
        accessorFn: (row) => (row.partialClasses ? 'X' : ''),
        size: 125,
        type: 'boolean',
      },
      {
        header: t('persons.title', { count: 2 }),
        id: 'countTeacher',
        accessorKey: 'countTeacher',
        size: 80,
        alignment: 'right',
      },
      {
        header: t('common.epochs'),
        accessorKey: 'teachingBlocks',
        id: 'teachingBlocks',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('common.week', { count: 2 }),
        id: 'teachingBlockWeeks',
        accessorKey: 'teachingBlockWeeks',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('factors.title', { count: 1 }),
        accessorFn: (row) => {
          return row.teachingBlockWeeks && (row.teachingBlockWeeks ?? 0) > 0
            ? formatNumber(row.deputat / row.teachingBlockWeeks)
            : 0;
        },
        cell: ({ row }) => {
          // hack hack hack BE liefert falsch
          return row.original.teachingBlockWeeks && (row.original.teachingBlockWeeks ?? 0) > 0
            ? formatNumber(row.original.deputat / row.original.teachingBlockWeeks)
            : 0;
        },
        id: 'factor',
        size: 90,
        alignment: 'right',
      },
      {
        header: t('deputate.title', { count: 1 }),
        accessorFn: (row) => formatNumber(row.deputat),
        size: 90,
        id: 'sum',
        alignment: 'right',
      },
    ];

    const additionalColumns: TableColumns<DeputatesDetailTableData>[] = [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'comment',
        size: 350,
      },
      {
        header: t('lesson.table.subject'),
        id: 'subjectDisplayName',
        accessorKey: 'subjectDisplayName',
        size: 200,
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'lessonClassesDisplayName',
        accessorKey: 'lessonClassesDisplayName',
        size: 200,
      },
      {
        header: t('deputate.title', { count: 1 }),
        accessorFn: (row) => formatNumber(row.deputat),
        id: 'deputat',
        size: 90,
        alignment: 'right',
      },
    ];

    const tables: ReactNode[] = [
      <div key='fs'>
        <div className={styles['table-header']}>
          <div>{t('deputate.deputateSubject')}</div>
          <div>{formatNumber(deputateData.sumSubjectLessons)}</div>
        </div>
        <Table<DeputatesDetailTableData>
          className={styles.table}
          columns={detailColumns}
          data={deputateData.subjectLessons ?? []}
          emptyStateSettings={{ hideIcon: true, subtitle: '' }}
          customPadding='var(--spacing-6)'
        />
      </div>,
    ];

    deputateData.subjectContainerLessons.forEach((data, key) =>
      tables.push(
        <div key={'table_' + key}>
          <div className={styles['table-header']}>
            <div>{t('deputate.evaluate.subjectContainer', { container: key })}</div>
            <div>{formatNumber(deputateData.sumSubjectContainerLessons.get(key) ?? 0)}</div>
          </div>
          <Table<DeputatesDetailTableData>
            className={styles.table}
            columns={containerColumns}
            data={data ?? []}
            emptyStateSettings={{ hideIcon: true, subtitle: '' }}
            customPadding='var(--spacing-6)'
          />
        </div>,
      ),
    );

    if (deputateData.additional.length > 0) {
      tables.push(
        <div key={'add'}>
          <div className={styles['table-header']}>
            <div>{t('deputate.deputateManually')}</div>
            <div>{formatNumber(deputateData.sumAdditional)}</div>
          </div>
          <Table<DeputatesDetailTableData>
            className={styles.table}
            columns={additionalColumns}
            data={deputateData.additional ?? []}
            emptyStateSettings={{ hideIcon: true, subtitle: '' }}
            customPadding='var(--spacing-6)'
          />
        </div>,
      );
    }

    const billedHours = _evalBillingMethod({
      deputateLesson:
        deputateData.sumSubjectLessons +
        [...deputateData.sumSubjectContainerLessons.values()].reduce((sum, cur) => sum + cur, 0),
      deputateManually: deputateData.sumAdditional ?? 0,
      deputateContract: currentContract?.hoursWeekly ?? 0,
      billingMethod: currentContract?.billingMethod ?? BillingMethods.GivenHours,
      limitedToFullTime: currentContract?.limitedToFullTime ?? false,
      fullTimeHours: useUserConfigContext().selectedSchoolYear?.fullTimeHours,
    });

    const isEmpty =
      (deputateData.subjectLessons === undefined || deputateData.subjectLessons?.length === 0) &&
      deputateData.additional.length === 0;

    return (
      <Card contentPadding='none' className={styles['planned-deputate-teacher-overview']}>
        <div className={styles.header}>
          <div className={styles.name}>{currentTeacher?.displayName}</div>
          <Tooltip content={color?.label ?? '-'}>
            <div
              className={styles['color-dot']}
              style={{
                backgroundColor: color?.html ?? 'white',
              }}
            ></div>
          </Tooltip>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <div>{t('deputate.deputateSubject')}</div>
            <div>{formatNumber(deputateData.sumSubjectLessons)}</div>
          </div>

          {[...deputateData.subjectContainerLessons.keys()].map((key) => {
            return (
              <div key={key} className={styles.stat}>
                <div>{t('deputate.evaluate.subjectContainer', { container: key })}</div>
                <div>{formatNumber(deputateData.sumSubjectContainerLessons.get(key) ?? 0)}</div>
              </div>
            );
          })}

          <div className={styles.stat}>
            <div>{t('deputate.deputateManually')}</div>
            <div>{formatNumber(deputateData.sumAdditional)}</div>
          </div>

          <div className={styles.stat}>
            <div>{t('common.sum')}</div>
            <div>{formatNumber(deputateData.sumTotal)}</div>
          </div>
        </div>
        <div className={styles.stats}>
          <div className={styles.stat}>
            <div>{t('contracts.contract')}</div>
            <div>{formatNumber(currentContract?.hoursWeekly ?? 0)}</div>
          </div>

          <div className={styles.stat}>
            <div>{t('deputate.payed')}</div>
            <div>{formatNumber(billedHours ?? 0)}</div>
          </div>
        </div>

        <div className={classNames(styles.tables, { [styles.empty]: isEmpty })}>
          {isEmpty ? <EmptyState title={t('deputate.evaluate.empty')} /> : tables}
        </div>
      </Card>
    );
  },
);
